import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { WifiOff, Wifi, RotateCw } from "lucide-react";
import { useOnlineStatus } from "@/lib/useOnlineStatus";

interface Props {
  /** Called when the user clicks retry, or automatically once when network returns. */
  onRetry: () => void;
  /** Visible label for what we were trying to load, e.g. "this page" or "the Save step". */
  context?: string;
  /** Optional compact rendering for in-tab error boundaries. */
  compact?: boolean;
}

/**
 * "You appear to be offline" fallback. Listens for the browser `online`
 * event and:
 *   - re-enables the Retry button immediately
 *   - auto-retries exactly once when the connection comes back, so users
 *     don't have to find the button after their wifi reconnects
 *
 * Use this from error boundaries when a chunk-load fails AND
 * navigator.onLine is false — it's far more actionable than a generic
 * "Something went wrong" message.
 */
export function OfflineFallback({ onRetry, context = "this content", compact }: Props) {
  const online = useOnlineStatus();
  const autoRetried = useRef(false);

  useEffect(() => {
    if (online && !autoRetried.current) {
      autoRetried.current = true;
      // Small delay so the UI can transition out of the offline state first.
      const id = window.setTimeout(onRetry, 400);
      return () => window.clearTimeout(id);
    }
  }, [online, onRetry]);

  if (compact) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="rounded-lg border border-border/60 bg-muted/30 p-4 space-y-3"
      >
        <div className="flex items-center gap-2">
          {online ? (
            <Wifi className="h-4 w-4 text-primary" />
          ) : (
            <WifiOff className="h-4 w-4 text-destructive" />
          )}
          <p className="text-sm font-medium">
            {online ? "Back online" : "You appear to be offline"}
          </p>
        </div>
        <p className="text-xs text-muted-foreground">
          {online
            ? `Reloading ${context}…`
            : `${context.charAt(0).toUpperCase() + context.slice(1)} can't be loaded without a connection. We'll retry automatically when you're back online.`}
        </p>
        <Button size="sm" onClick={onRetry} disabled={!online} className="gap-2">
          <RotateCw className="h-3.5 w-3.5" /> {online ? "Retry now" : "Waiting for connection…"}
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto" role="status" aria-live="polite">
      <Card className="border-border/50">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-destructive/10 flex items-center justify-center shrink-0">
              {online ? (
                <Wifi className="h-5 w-5 text-primary" />
              ) : (
                <WifiOff className="h-5 w-5 text-destructive" />
              )}
            </div>
            <div className="min-w-0">
              <CardTitle className="font-display text-lg">
                {online ? "Back online" : "You appear to be offline"}
              </CardTitle>
              <CardDescription>
                {online
                  ? `Reloading ${context} now.`
                  : `We couldn't load ${context} because your device is offline. We'll retry automatically as soon as the connection is back.`}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Button onClick={onRetry} disabled={!online} className="gap-2">
            <RotateCw className="h-4 w-4" /> {online ? "Retry now" : "Waiting for connection…"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
